import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  activateUserEmail,
  authWithGoogle,
  createUser,
  isRefreshSessionUnavailable,
  refreshAccessToken,
  signInWithEmailAndPassword,
  signOut,
} from '@/services/authService';
import { getUser } from '@/services/userService';
import api, { setAuthSessionHandlers } from '@/services/api';
import { clearOnboardingStatusCache } from '@/services/onboardingStatusCache';
import { jwtDecode } from 'jwt-decode';
import { User } from '@/types/user';
import { identifyOnAuth, resetUser } from '@/lib/posthog';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  token: string | null;
  fetchProfile: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (credential: string) => Promise<void>;
  signUp: (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
  ) => Promise<void>;
  activateEmail: (uid: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  /* ---------------- STATE ---------------- */

  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = Boolean(token);

  const clearSession = useCallback(() => {
    delete api.defaults.headers.common.Authorization;
    setUser(null);
    setToken(null);
    clearOnboardingStatusCache();
    resetUser();
  }, []);

  const expireSession = useCallback((error?: unknown) => {
    if (error && !isRefreshSessionUnavailable(error)) {
      console.error('Failed to refresh the session:', error);
    }
    clearSession();
    window.location.assign('/login?reason=session_expired');
  }, [clearSession]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `JWT ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setAuthSessionHandlers(null);
      return;
    }

    setAuthSessionHandlers({
      refreshAccessToken: async () => {
        const { data } = await refreshAccessToken();
        setToken(data.access);
        return data.access;
      },
      onSessionExpired: expireSession,
    });

    return () => setAuthSessionHandlers(null);
  }, [expireSession, token]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await refreshAccessToken();
        setToken(data.access);
      } catch (error) {
        // A missing/expired refresh cookie is the normal state for a visitor on
        // Login or Sign up. Network/server failures are unexpected and remain
        // visible in diagnostics instead of becoming unhandled rejections.
        if (!isRefreshSessionUnavailable(error)) {
          console.error('Failed to restore the session:', error);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);


  useEffect(() => {
    if (!token || user) return;

    (async () => {
      try {
        const { data } = await getUser(token);
        identifyOnAuth(data);
        setUser(data);
      } catch (error) {
        console.error('Failed to load the authenticated user:', error);
        clearSession();
      }
    })();
  }, [clearSession, token, user]);


  useEffect(() => {
    if (!token) return;

    const { exp } = jwtDecode<{ exp: number }>(token);
    const delay = Math.max(exp * 1000 - Date.now() - 60_000, 0);

    const id = setTimeout(async () => {
      try {
        const { data } = await refreshAccessToken();
        setToken(data.access);
      } catch (error) {
        expireSession(error);
      }
    }, delay);

    return () => clearTimeout(id);
  }, [expireSession, token]);


  const fetchProfile = useCallback(async () => {
    if (!token) return;
    const { data } = await getUser(token);
    identifyOnAuth(data);
    setUser(data);
  }, [token]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      // Cached onboarding state belongs to whoever was signed in before. It is
      // module-level, so without this a second account signing in on the same
      // tab would be routed by the first account's status (AITS-590).
      clearOnboardingStatusCache();
      const { data } = await signInWithEmailAndPassword(email, password);
      setToken(data.access);
      setLoading(false);
    },
    [],
  );

  const signInWithGoogleHandler = useCallback(
    async (credential: string) => {
      setLoading(true);
      clearOnboardingStatusCache();
      const { data } = await authWithGoogle(credential);
      setToken(data.access);
      setLoading(false);
    },
    [],
  );

  const signUp = useCallback(
    async (firstName: string, lastName: string, email: string, password: string) => {
      await createUser(firstName, lastName, email, password);
    },
    [],
  );

  const activateEmailHandler = useCallback(async (uid: string, token: string) => {
    await activateUserEmail(uid, token);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await signOut(token);
      }
    } finally {
      // Once logout has finished (including any 401 refresh/retry it needed),
      // late requests must not turn deliberate sign-out into a misleading
      // "session expired" redirect.
      setAuthSessionHandlers(null);
      // The local session must always end, even if the server-side token was
      // already expired or the logout request could not reach the API.
      clearSession();
    }
  }, [clearSession, token]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      loading,
      isAuthenticated,
      token,
      fetchProfile,
      signIn,
      signInWithGoogle: signInWithGoogleHandler,
      signUp,
      activateEmail: activateEmailHandler,
      logout,
    }),
    [
      user,
      loading,
      isAuthenticated,
      token,
      fetchProfile,
      signIn,
      signInWithGoogleHandler,
      signUp,
      activateEmailHandler,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};
